/* eslint-disable react-doctor/only-export-components */
import { createRootRoute, HeadContent, Outlet } from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "sonner";
import { GenericError } from "@/components/Error";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { config } from "@/data/config";
import {
	ORGANIZATION_DESCRIPTION,
	SITE_DISPLAY_NAME,
	SITE_URL,
	SUPPORT_EMAIL,
} from "@/data/site-metadata";
import mainCss from "@/styles/main.css?url";
import { canonicalUrl } from "@/utils/seo";

const websiteSchema = {
	"@context": "https://schema.org",
	"@type": "WebSite",
	name: SITE_DISPLAY_NAME,
	url: SITE_URL,
	description: ORGANIZATION_DESCRIPTION,
	potentialAction: {
		"@type": "SearchAction",
		target: `${SITE_URL}/tools/dns-lookup?domain={search_term_string}`,
		"query-input": "required name=search_term_string",
	},
};

const organizationSchema = {
	"@context": "https://schema.org",
	"@type": "Organization",
	name: SITE_DISPLAY_NAME,
	url: SITE_URL,
	description: ORGANIZATION_DESCRIPTION,
	contactPoint: {
		"@type": "ContactPoint",
		email: SUPPORT_EMAIL,
		contactType: "customer support",
	},
};

const TanStackRouterDevtools = import.meta.env.DEV
	? lazy(() =>
			import("@tanstack/react-router-devtools").then((mod) => ({
				default: mod.TanStackRouterDevtools,
			})),
		)
	: null;

function RootRouteError({ error, reset }: { error: Error; reset: () => void }) {
	useEffect(() => {
		void import("@sentry/react").then((Sentry) => {
			Sentry.captureException(error);
		});
	}, [error]);
	return <GenericError retry={reset} />;
}

export const Route = createRootRoute({
	head: ({ matches }) => {
		const pathname = matches.at(-1)?.pathname ?? "/";
		return {
			meta: [
				// viewport is static in index.html so it applies before hydration
				{
					name: "apple-mobile-web-app-title",
					content: config.siteName,
				},
			],
			links: [
				{ rel: "canonical", href: canonicalUrl(pathname) },
				{ rel: "stylesheet", href: mainCss },
				{
					rel: "apple-touch-icon",
					sizes: "180x180",
					href: "/apple-touch-icon.png",
				},
				{
					rel: "icon",
					type: "image/png",
					sizes: "32x32",
					href: "/favicon-32x32.png",
				},
				{
					rel: "icon",
					type: "image/png",
					sizes: "16x16",
					href: "/favicon-16x16.png",
				},
				{ rel: "manifest", href: "/manifest.json" },
				{ rel: "icon", href: "/favicon.ico" },
			],
			scripts: [
				{
					type: "application/ld+json",
					children: JSON.stringify(websiteSchema),
				},
				{
					type: "application/ld+json",
					children: JSON.stringify(organizationSchema),
				},
			],
		};
	},
	errorComponent: RootRouteError,

	component: RootLayout,
});

function RootLayout() {
	return (
		<>
			{/*
			 * Rendered unconditionally: route head() definitions must also apply
			 * during client-side navigation, otherwise title/meta stay frozen at
			 * the first page loaded. React 19 hoists title/meta/link into <head>.
			 */}
			<HeadContent />
			<ThemeProvider>
				<Outlet />
				{import.meta.env.DEV && TanStackRouterDevtools ? (
					<Suspense fallback={null}>
						<TanStackRouterDevtools position="bottom-right" />
					</Suspense>
				) : null}
				<Toaster richColors />
			</ThemeProvider>
		</>
	);
}
